
import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Star, ChevronRight, Mail } from 'lucide-react';

interface Skill {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface SkillSelectionProps {
  studentEmail: string;
  studentName: string;
  onSkillsSelected: (email: string, skills: string[]) => void;
  selectedSkills: string[];
  skillsList: Skill[];
}

const SkillSelection: React.FC<SkillSelectionProps> = ({
  studentEmail,
  studentName,
  onSkillsSelected,
  selectedSkills,
  skillsList,
}) => {
  const [localSelected, setLocalSelected] = useState<string[]>(selectedSkills);

  const toggleSkill = (skillId: string) => {
    setLocalSelected(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleSubmit = () => {
    if (localSelected.length === 0) {
      alert('Please select at least one skill');
      return;
    }
    onSkillsSelected(studentEmail, localSelected);
  };

  return (
    <div className="max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-teal-600 p-8 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Star className="w-8 h-8" />
            <h2 className="text-3xl font-bold">Choose Your Skills</h2>
          </div>
          <p className="text-green-100 text-lg">
            Welcome <span className="font-bold">{studentName}</span>! Select the computer skills you want to master
          </p>
          <div className="mt-4 flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
              <Mail className="w-4 h-4" />
              <span>{studentEmail}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-3 py-1.5 rounded-full">
              <CheckCircle className="w-4 h-4" />
              <span>{localSelected.length} selected</span>
            </div>
          </div>
        </div>

        <div className="p-8">
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Available Computer Skills
            </h3>
            <p className="text-gray-600">
              Select one or more skills you want to learn. You can always update your selection later.
            </p>
          </div>

          {/* Skills Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {skillsList.map((skill, index) => (
              <motion.button
                key={skill.id}
                onClick={() => toggleSkill(skill.id)}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`relative p-5 rounded-2xl border-2 text-left transition-all duration-300 ${
                  localSelected.includes(skill.id)
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md bg-white'
                }`}
              >
                {localSelected.includes(skill.id) && (
                  <div className="absolute top-3 right-3">
                    <CheckCircle className="w-5 h-5 text-blue-600" />
                  </div>
                )}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${skill.color} flex items-center justify-center mb-4 shadow-md`}>
                  <i className={`${skill.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-1 pr-6">{skill.name}</h4>
                <p className="text-xs text-gray-500">Click to {localSelected.includes(skill.id) ? 'remove' : 'select'}</p>
              </motion.button>
            ))}
          </div>

          {/* Selected Skills Summary */}
          {localSelected.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
            >
              <h4 className="font-bold text-gray-900 mb-2">Selected Skills:</h4>
              <div className="flex flex-wrap gap-2">
                {localSelected.map(skillId => {
                  const skill = skillsList.find(s => s.id === skillId);
                  return skill ? (
                    <span
                      key={skillId}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded-full text-sm font-medium text-gray-700 border border-gray-200"
                    >
                      <i className={`${skill.icon} text-blue-600`}></i>
                      {skill.name}
                      <button
                        type="button"
                        onClick={() => toggleSkill(skillId)}
                        className="ml-1 text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            </motion.div>
          )}

          {/* Submit Button */}
          <motion.button
            onClick={handleSubmit}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
          >
            Save My Skills Profile
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default SkillSelection;
